import axios from "axios";
import { Modal } from "antd";
import * as tokenService from "./tokenService";
import { AUTH_STORAGE_KEYS } from "./tokenService";

/**
 * Logout + session-expired handling.
 *
 * SHARED FILE — byte-identical in prx_shell-ui, prx_buyerapp-ui,
 * prx_buyer_WorkOrder_ui and prx_master-ui (canonical: prx_buyerapp-ui).
 *
 * Redirect target is /login on the shared origin (owned by the shell) — never
 * "/" (which bounced sub-path-mounted modules through the launcher). Nothing
 * here wipes state before the user sees what happened, and the localStorage
 * logout marker broadcasts the logout to every other tab and bundle.
 */

const API_BASE =
  process.env.REACT_APP_API_LOGIN_URL || process.env.REACT_APP_API_BASE_URL || "";

export type LogoutReason = "user" | "idle" | "session-expired" | "logged-out";

let logoutInProgress = false;
let sessionExpiredShown = false;

function loginUrl(reason: LogoutReason, withRedirect: boolean): string {
  const params = new URLSearchParams({ reason });
  if (withRedirect) {
    // pathname already carries the app's mount prefix (/p2p, /wom, /masters).
    params.set("redirect", window.location.pathname + window.location.search);
  }
  return `/login?${params.toString()}`;
}

export async function performLogout(reason: LogoutReason): Promise<void> {
  if (logoutInProgress) return;
  logoutInProgress = true;

  // Best-effort server-side revocation (refresh token + Redis session) —
  // must not block the local logout if the network or session is dead.
  try {
    const authHeader = tokenService.getAccessTokenRaw();
    if (authHeader) {
      await axios
        .post(
          `${API_BASE}/auth/logout`,
          {},
          { withCredentials: true, headers: { Authorization: authHeader } }
        )
        .catch(() => undefined);
    }
  } catch {
    /* ignore */
  }

  try {
    tokenService.clearTokens();
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    /* ignore */
  }

  // Set AFTER the clears so the storage event other tabs receive is the
  // logout marker itself.
  try {
    localStorage.setItem(AUTH_STORAGE_KEYS.logoutAt, String(Date.now()));
  } catch {
    /* ignore */
  }

  window.location.assign(loginUrl(reason, reason !== "user"));
}

/**
 * Terminal session failure (refresh token dead): tell the user, then log out.
 * No sleep-and-wipe, no redirect before acknowledgement — form state survives
 * until the user (or the 15s fallback) moves on.
 */
export function handleSessionExpired(): void {
  if (sessionExpiredShown || logoutInProgress) return;
  sessionExpiredShown = true;

  let done = false;
  const signIn = () => {
    if (done) return;
    done = true;
    void performLogout("session-expired");
  };

  Modal.warning({
    title: "Session expired",
    content: "Your session has expired. Please sign in again.",
    okText: "Sign in",
    zIndex: 2100,
    onOk: signIn,
  });
  window.setTimeout(signIn, 15_000);
}

/** Logout in any other tab/bundle → clear local state and go to /login. */
export function subscribeToLogoutBroadcast(): () => void {
  const handler = (event: StorageEvent) => {
    if (event.key !== AUTH_STORAGE_KEYS.logoutAt || !event.newValue) return;
    const path = window.location.pathname;
    if (path.startsWith("/login") || path.includes("/public-links/")) return;
    if (logoutInProgress) return;
    logoutInProgress = true; // the other tab already revoked server-side
    try {
      tokenService.clearTokens();
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    window.location.assign("/login?reason=logged-out");
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
